import type {
  EsocialData,
  ReciboGroup,
  TotalizadoEsocial,
  TotalizadorGroup,
} from "@/types/analise-previdenciario"

export function groupByRecibo(data: EsocialData): ReciboGroup[] {
  const reciboMap = new Map<string, ReciboGroup>()

  // Collect all unique nrRecibo from rubricas and verbas (they share the same recibo)
  const allRecibos = new Set<string>()
  data.rubricas.forEach((r) => allRecibos.add(r.nrRecibo))
  data.verbas.forEach((v) => allRecibos.add(v.nrRecibo_verba))
  data.totalizadosEsocial.forEach((t) => allRecibos.add(t.nrRecibo))

  allRecibos.forEach((nrRecibo) => {
    const rubricas = data.rubricas.filter((r) => r.nrRecibo === nrRecibo)
    const verbas = data.verbas.filter((v) => v.nrRecibo_verba === nrRecibo)
    const totalizadoresDoRecibo = data.totalizadosEsocial.filter((t) => t.nrRecibo === nrRecibo)
    const totalizador =
      totalizadoresDoRecibo.find((t) => t.isRetorno === true) ||
      totalizadoresDoRecibo.find((t) => isTotalizadorDeRetorno(t)) ||
      totalizadoresDoRecibo[0] ||
      null

    // Pagamentos link via ideDmDev (same ideDmDev as the recibo's rubricas/verbas)
    const ideDmDevs = new Set<string>()
    rubricas.forEach((r) => ideDmDevs.add(r.ideDmDev))
    verbas.forEach((v) => ideDmDevs.add(v.ideDmDev))

    const pagamentos = data.esocialPagamentos.filter((p) =>
      ideDmDevs.has(p.ideDmDev)
    )

    const ideDmDev = rubricas[0]?.ideDmDev || verbas[0]?.ideDmDev || ""
    const dhProcessamento =
      totalizador?.dhProcessamento ||
      totalizador?.perApur ||
      rubricas[0]?.dhProcessamento ||
      verbas[0]?.dhProcessamento ||
      ""

    reciboMap.set(nrRecibo, {
      nrRecibo,
      rubricas,
      verbas,
      totalizador,
      pagamentos,
      ideDmDev,
      dhProcessamento,
    })
  })

  // Sort by date descending
  return Array.from(reciboMap.values()).sort(
    (a, b) =>
      parseDateValue(b.dhProcessamento).getTime() -
      parseDateValue(a.dhProcessamento).getTime()
  )
}

export function groupByPeriodoCpf(data: EsocialData): TotalizadorGroup[] {
  const groupMap = new Map<string, TotalizadorGroup>()
  const reciboGroups = groupByRecibo(data)

  data.totalizadosEsocial.forEach((t) => {
    const key = `${t.perApur}|${t.cpfTrabalhador}`

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        perApur: t.perApur,
        cpfTrabalhador: t.cpfTrabalhador,
        nrRecibos: [],
        totalizadores: [],
        pagamentos: [],
        recibos: [],
      })
    }

    const group = groupMap.get(key)!
    group.totalizadores.push(t)

    if (!group.nrRecibos.includes(t.nrRecibo)) {
      group.nrRecibos.push(t.nrRecibo)
    }

    const recibo = reciboGroups.find((rg) => rg.totalizador?.nrRecibo === t.nrRecibo)
    if (recibo && !group.recibos.some((r) => r.nrRecibo === recibo.nrRecibo)) {
      group.recibos.push(recibo)
    }
  })

  for (const group of groupMap.values()) {
    const ideDmDevs = new Set(group.recibos.map((recibo) => recibo.ideDmDev).filter(Boolean))

    group.pagamentos =
      ideDmDevs.size === 0
        ? []
        : data.esocialPagamentos.filter((pagamento) => ideDmDevs.has(pagamento.ideDmDev))
  }

  // Sort by perApur descending
  return Array.from(groupMap.values()).sort(
    (a, b) =>
      parseDateValue(b.perApur).getTime() - parseDateValue(a.perApur).getTime()
  )
}

export function isTotalizadorDeRetorno(
  totalizador: Pick<TotalizadoEsocial, "isRetificado" | "isRetorno">
): boolean {
  return totalizador.isRetorno === true || totalizador.isRetificado === true
}

export function formatCurrency(value: string | number): string {
  const num = typeof value === "string" ? parseFloat(value) : value
  return num.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  })
}

export function formatCpf(cpf: string): string {
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
}

export function formatDate(dateStr: string): string {
  const date = parseDateValue(dateStr)
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

export function formatDateTime(dateStr: string): string {
  const date = parseDateValue(dateStr)
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function formatPeriodo(perApur: string): string {
  const date = parseDateValue(perApur)
  return date.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric",
  })
}

function parseDateValue(value: string): Date {
  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/
  if (dateOnlyPattern.test(value)) {
    const [year, month, day] = value.split("-").map(Number)
    return new Date(year, month - 1, day, 12, 0, 0)
  }
  return new Date(value)
}

export function truncateRecibo(nrRecibo: string): string {
  // Show last 8 chars for brevity
  if (nrRecibo.length > 16) {
    return `...${nrRecibo.slice(-10)}`
  }
  return nrRecibo
}

export function getNatRubrLabel(natRubr: string): string {
  const map: Record<string, string> = {
    "1023": "Remuneracao",
    "1024": "Ferias",
    "6001": "13o Salario Indenizado",
    "6003": "Aviso Previo Indenizado",
    "6006": "Ferias Proporcionais",
  }
  return map[natRubr] || natRubr
}

export function getNatRubrColor(natRubr: string): string {
  const map: Record<string, string> = {
    "1023": "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    "1024": "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300",
    "6001": "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    "6003": "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300",
    "6006": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300",
  }
  return map[natRubr] || "bg-muted text-muted-foreground"
}
